import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * useCollectedVariables — acumula variáveis populadas pelo agente entre turnos
 * do playground e expõe quais mudaram no último turno (para badges "novo").
 *
 * Fontes acumuladas:
 *   - variables        — state.variables do agente (filtra chaves _internas)
 *   - collectedData    — state.collected_data (filtra chaves _internas)
 *   - apiResponses     — state.api_responses (debug-only; opcional)
 *
 * Também guarda o "esqueleto" (declaredVariables) entregue pelo backend ao
 * varrer o graph_definition — usado para mostrar variáveis declaradas mas
 * ainda não populadas (com placeholder).
 *
 * Reset automático quando `agentId` muda (evita misturar conversas de
 * agentes diferentes).
 */
const isInternal = (key) => typeof key === 'string' && key.startsWith('_');

const filterInternalKeys = (obj) => {
  if (!obj || typeof obj !== 'object') return {};
  const out = {};
  Object.entries(obj).forEach(([k, v]) => {
    if (!isInternal(k)) out[k] = v;
  });
  return out;
};

const useCollectedVariables = (agentId) => {
  const [variables, setVariables] = useState({});
  const [collectedData, setCollectedData] = useState({});
  const [apiResponses, setApiResponses] = useState({});
  const [declaredVariables, setDeclaredVariables] = useState([]);
  const [recentlyChangedKeys, setRecentlyChangedKeys] = useState(new Set());

  // Tracking de turno anterior para detectar diff
  const previousSnapshot = useRef({ variables: {}, collectedData: {}, apiResponses: {} });

  // Reset ao trocar de agente
  useEffect(() => {
    setVariables({});
    setCollectedData({});
    setApiResponses({});
    setDeclaredVariables([]);
    setRecentlyChangedKeys(new Set());
    previousSnapshot.current = { variables: {}, collectedData: {}, apiResponses: {} };
  }, [agentId]);

  /**
   * Atualiza com o response do playgroundChat.
   * Espera campos: variables, collected_data, api_responses, declared_variables.
   */
  const updateFromResponse = useCallback((response) => {
    if (!response || typeof response !== 'object') return;

    const newVariables = filterInternalKeys(response.variables);
    const newCollected = filterInternalKeys(response.collected_data);
    const newApiResp = response.api_responses || {};
    const newDeclared = Array.isArray(response.declared_variables)
      ? response.declared_variables
      : null;

    // Calcula diff entre snapshot anterior e novo (chaves alteradas neste turno)
    const changed = new Set();
    const checkChanges = (prev, next) => {
      Object.entries(next).forEach(([k, v]) => {
        if (JSON.stringify(prev[k]) !== JSON.stringify(v)) {
          changed.add(k);
        }
      });
    };
    checkChanges(previousSnapshot.current.variables, newVariables);
    checkChanges(previousSnapshot.current.collectedData, newCollected);
    checkChanges(previousSnapshot.current.apiResponses, newApiResp);

    setVariables((prev) => ({ ...prev, ...newVariables }));
    setCollectedData((prev) => ({ ...prev, ...newCollected }));
    setApiResponses((prev) => ({ ...prev, ...newApiResp }));
    if (newDeclared !== null) {
      // Última declaração vence — se o usuário mudou o grafo no meio do teste,
      // reflete o esqueleto atual.
      setDeclaredVariables(newDeclared);
    }
    setRecentlyChangedKeys(changed);

    previousSnapshot.current = {
      variables: { ...previousSnapshot.current.variables, ...newVariables },
      collectedData: { ...previousSnapshot.current.collectedData, ...newCollected },
      apiResponses: { ...previousSnapshot.current.apiResponses, ...newApiResp },
    };
  }, []);

  /**
   * Limpa todo o estado acumulado — usar ao limpar a conversa.
   */
  const reset = useCallback(() => {
    setVariables({});
    setCollectedData({});
    setApiResponses({});
    setRecentlyChangedKeys(new Set());
    previousSnapshot.current = { variables: {}, collectedData: {}, apiResponses: {} };
    // declaredVariables NÃO reseta — vem do grafo, é "esqueleto" estático
  }, []);

  return {
    variables,
    collectedData,
    apiResponses,
    declaredVariables,
    recentlyChangedKeys,
    updateFromResponse,
    reset,
  };
};

export default useCollectedVariables;
